import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateTimePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useCreateEvent, useUsers, useContacts, useAccounts } from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { useUser } from '@/contexts/UserContext'
import type { Event } from '@/types'
import { Calendar, Clock, MapPin, Users } from 'lucide-react'
import { explainError } from '@/lib/explain-error'

interface CreateEventModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDate?: Date
  relatedToId?: string // Pre-fill what_id
  relatedToType?: string // "Lead", "Account", "Opportunity", etc.
  relatedToName?: string // Display name
  ownerId?: string // Pre-fill owner
}

export function CreateEventModal({
  open,
  onOpenChange,
  initialDate,
  relatedToId,
  relatedToType,
  relatedToName,
  ownerId,
}: CreateEventModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateEvent()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: contactsData } = useContacts({ limit: 10000 })
  const { data: accountsData } = useAccounts({ limit: 10000 })
  const eventTypes = usePicklistOptions('event', 'type')
  const showAsOptions = usePicklistOptions('event', 'show_as')

  const users = usersData?.data || []
  const contacts = contactsData?.data || []
  const accounts = accountsData?.data || []

  const getDefaultStartDate = () => {
    if (initialDate) {
      const date = new Date(initialDate)
      date.setHours(9, 0, 0, 0)
      return date.toISOString().slice(0, 16)
    }
    const now = new Date()
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15) // Round to next 15 minutes
    now.setSeconds(0, 0)
    return now.toISOString().slice(0, 16)
  }

  const getDefaultEndDate = () => {
    const start = new Date(getDefaultStartDate())
    start.setHours(start.getHours() + 1) // Default 1 hour duration
    return start.toISOString().slice(0, 16)
  }

  const [formData, setFormData] = useState<Partial<Event> & { who_type?: string }>({
    subject: '',
    type: 'Meeting',
    start_date_time: getDefaultStartDate(),
    end_date_time: getDefaultEndDate(),
    location: '',
    description: '',
    owner_id: '',
    who_id: '',
    who_type: '',
    what_id: '',
    is_all_day_event: false,
    is_private: false,
    show_as: 'Busy',
    reminder_date_time: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-populate form when modal opens with relatedTo props.
  // Leads and contacts are the "who" (who_id); accounts and opportunities are the "what" (what_id).
  useEffect(() => {
    if (open) {
      const isWhoEntity = relatedToType === 'Lead' || relatedToType === 'Contact'
      setFormData((prev) => ({
        ...prev,
        who_id: isWhoEntity ? relatedToId || prev.who_id || '' : prev.who_id || '',
        who_type: isWhoEntity ? relatedToType || prev.who_type || '' : prev.who_type || '',
        what_id: isWhoEntity ? prev.what_id || '' : relatedToId || prev.what_id || '',
        owner_id: ownerId || currentUser?.id || prev.owner_id || '',
      }))
    }
  }, [open, relatedToId, relatedToType, ownerId, currentUser?.id])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.subject?.trim()) {
      newErrors.subject = 'Subject is required'
    }

    if (!formData.type) {
      newErrors.type = 'Type is required'
    }

    if (!formData.start_date_time) {
      newErrors.start_date_time = 'Start date/time is required'
    }

    if (!formData.end_date_time) {
      newErrors.end_date_time = 'End date/time is required'
    }

    if (formData.start_date_time && formData.end_date_time) {
      const start = new Date(formData.start_date_time)
      const end = new Date(formData.end_date_time)
      if (end <= start) {
        newErrors.end_date_time = 'End time must be after start time'
      }
    }

    if (!formData.owner_id) {
      newErrors.owner_id = 'Owner is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Convert datetime-local values to ISO strings with timezone
      const eventData: Partial<Event> = {
        ...formData,
        start_date_time: formData.start_date_time
          ? new Date(formData.start_date_time).toISOString()
          : '',
        end_date_time: formData.end_date_time ? new Date(formData.end_date_time).toISOString() : '',
        reminder_date_time: formData.reminder_date_time
          ? new Date(formData.reminder_date_time).toISOString()
          : undefined,
      }

      await createMutation.mutateAsync(eventData)

      toast({
        title: 'Success',
        description: 'Event created successfully.',
      })

      // Reset form and close modal
      setFormData({
        subject: '',
        type: 'Meeting',
        start_date_time: getDefaultStartDate(),
        end_date_time: getDefaultEndDate(),
        location: '',
        description: '',
        owner_id: ownerId || currentUser?.id || '',
        who_id: '',
        what_id: relatedToId || '',
        is_all_day_event: false,
        is_private: false,
        show_as: 'Busy',
        reminder_date_time: '',
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create event')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value }

      // Auto-adjust end time when start time changes
      if (field === 'start_date_time' && value) {
        const start = new Date(value)
        const currentEnd = new Date(prev.end_date_time || value)
        const currentDuration =
          currentEnd.getTime() - new Date(prev.start_date_time || value).getTime()

        // Keep the same duration when changing start time
        if (currentDuration > 0) {
          const newEnd = new Date(start.getTime() + currentDuration)
          updated.end_date_time = newEnd.toISOString().slice(0, 16)
        } else {
          // Default 1 hour duration
          start.setHours(start.getHours() + 1)
          updated.end_date_time = start.toISOString().slice(0, 16)
        }
      }

      // Handle all-day event toggle
      if (field === 'is_all_day_event') {
        if (value === true) {
          // For all-day events, set times to start and end of day
          const start = new Date(updated.start_date_time || new Date())
          start.setHours(0, 0, 0, 0)
          updated.start_date_time = start.toISOString().slice(0, 16)

          const end = new Date(start)
          end.setHours(23, 59, 0, 0)
          updated.end_date_time = end.toISOString().slice(0, 16)
        }
      }

      return updated
    })

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="create-event-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Event</DialogTitle>
          <DialogDescription id="create-event-description">
            Schedule a new event or meeting. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subject and Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="subject" className="required">
                Subject *
              </Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Enter event subject"
                aria-required="true"
                aria-invalid={!!errors.subject}
                aria-describedby={errors.subject ? 'subject-error' : undefined}
              />
              {errors.subject && (
                <p id="subject-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.subject}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="type" className="required">
                Type *
              </Label>
              <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
                <SelectTrigger id="type" aria-required="true">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-destructive mt-1" role="alert">
                  {errors.type}
                </p>
              )}
            </div>
          </div>

          {/* All-day event toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="all-day"
              checked={formData.is_all_day_event}
              onCheckedChange={(checked) => handleChange('is_all_day_event', checked)}
            />
            <Label htmlFor="all-day">All-day event</Label>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start" className="required flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Start Date/Time *
              </Label>
              <DateTimePicker
                id="start"
                value={formData.start_date_time}
                onChange={(v) => handleChange('start_date_time', v ?? '')}
                aria-label="Start Date/Time"
                aria-required="true"
                aria-invalid={!!errors.start_date_time}
                aria-describedby={errors.start_date_time ? 'start-error' : undefined}
              />
              {errors.start_date_time && (
                <p id="start-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.start_date_time}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="end" className="required flex items-center gap-1">
                <Clock className="h-3 w-3" />
                End Date/Time *
              </Label>
              <DateTimePicker
                id="end"
                value={formData.end_date_time}
                onChange={(v) => handleChange('end_date_time', v ?? '')}
                aria-label="End Date/Time"
                aria-required="true"
                aria-invalid={!!errors.end_date_time}
                aria-describedby={errors.end_date_time ? 'end-error' : undefined}
              />
              {errors.end_date_time && (
                <p id="end-error" className="text-sm text-destructive mt-1" role="alert">
                  {errors.end_date_time}
                </p>
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <Label htmlFor="location" className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Location
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="Enter location or meeting link"
            />
          </div>

          {/* Owner and Show As */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner" className="required flex items-center gap-1">
                <Users className="h-3 w-3" />
                Owner *
              </Label>
              <Select
                value={formData.owner_id}
                onValueChange={(value) => handleChange('owner_id', value)}
              >
                <SelectTrigger id="owner" aria-required="true" aria-invalid={!!errors.owner_id}>
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent>
                  {[...users]
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.owner_id && (
                <p className="text-sm text-destructive mt-1" role="alert">
                  {errors.owner_id}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="show-as">Show As</Label>
              <Select
                value={formData.show_as}
                onValueChange={(value) => handleChange('show_as', value)}
              >
                <SelectTrigger id="show-as">
                  <SelectValue placeholder="Select availability" />
                </SelectTrigger>
                <SelectContent>
                  {showAsOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Related To */}
          {relatedToName && (
            <div className="p-3 bg-muted rounded-md">
              <Label className="text-xs text-muted-foreground">Related To ({relatedToType})</Label>
              <p className="text-sm font-medium">{relatedToName}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact">Related Contact</Label>
              <Select
                value={formData.who_id || '__NONE__'}
                onValueChange={(value) =>
                  handleChange('who_id', value === '__NONE__' ? null : value)
                }
              >
                <SelectTrigger id="contact">
                  <SelectValue placeholder="Select contact (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">None</SelectItem>
                  {[...contacts]
                    .sort(
                      (a, b) =>
                        (a.last_name || '').localeCompare(b.last_name || '') ||
                        (a.first_name || '').localeCompare(b.first_name || '')
                    )
                    .map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="account">Related Account</Label>
              <Select
                value={formData.what_id || '__NONE__'}
                onValueChange={(value) =>
                  handleChange('what_id', value === '__NONE__' ? null : value)
                }
              >
                <SelectTrigger id="account">
                  <SelectValue placeholder="Select account (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">None</SelectItem>
                  {[...accounts]
                    .sort((a, b) =>
                      (a.name || a.account_name || '').localeCompare(b.name || b.account_name || '')
                    )
                    .map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name || account.account_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reminder */}
          <div>
            <Label htmlFor="reminder">Reminder</Label>
            <DateTimePicker
              id="reminder"
              value={formData.reminder_date_time}
              onChange={(v) => handleChange('reminder_date_time', v ?? '')}
              aria-label="Reminder"
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter event description or agenda"
              rows={4}
            />
          </div>

          {/* Privacy toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="private"
              checked={formData.is_private}
              onCheckedChange={(checked) => handleChange('is_private', checked)}
            />
            <Label htmlFor="private">Private event</Label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
